/**
 * OrderQueueSettingsSection - Configure order queue display and pagination
 *
 * Allows configuring:
 * - Display Mode: Traditional pages or endless scroll
 * - Page Size: Number of orders to show per page (30, 50, 100, 200)
 * - Filter Tags: Which Shopify order tags appear in the order queue tag filter
 *
 * Settings are stored server-side in prod_settings and shared across all users at the location.
 * Follows the same query/mutation pattern as WorkflowSettingsSection.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Loader2, AlertTriangle, RefreshCw, Save, Search, X } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

type PaginationMode = 'paginated' | 'endless';

const PAGE_SIZE_OPTIONS = [30, 50, 100, 200] as const;

interface OrderQueueSettingsSectionProps {
  /** When true, renders without Card wrapper for embedding in accordions */
  embedded?: boolean;
}

export function OrderQueueSettingsSection({ embedded = false }: OrderQueueSettingsSectionProps) {
  const { t } = useTranslation('settings');
  const [paginationMode, setPaginationMode] = useState<PaginationMode>('paginated');
  const [pageSize, setPageSize] = useState(30);
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Tag search state
  const [tagSearchOpen, setTagSearchOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Queries
  const settingsQuery = trpc.productionSettings.getWorkflowSettings.useQuery();

  // Search tags on-demand (only when popover is open and user has typed)
  const tagSearchResults = trpc.productionSettings.searchOrderTags.useQuery(
    { query: debouncedQuery },
    { enabled: tagSearchOpen && debouncedQuery.length > 0 }
  );

  // Mutation
  const updateMutation = trpc.productionSettings.updateWorkflowSettings.useMutation({
    onSuccess: () => {
      toast.success(t('production.orderQueue.saved'));
      setHasChanges(false);
      settingsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to save: ${error.message}`);
    },
  });

  // Sync local state with server data
  useEffect(() => {
    if (settingsQuery.data) {
      setPaginationMode(settingsQuery.data.orderQueuePaginationMode);
      setPageSize(settingsQuery.data.orderQueuePageSize);
      setFilterTags(settingsQuery.data.filterTags);
      setHasChanges(false);
    }
  }, [settingsQuery.data]);

  // Debounce tag search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(tagSearchQuery);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [tagSearchQuery]);

  const handlePaginationModeChange = (value: PaginationMode) => {
    setPaginationMode(value);
    setHasChanges(true);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setHasChanges(true);
  };

  const handleAddTag = (tag: string) => {
    if (!filterTags.includes(tag)) {
      setFilterTags(prev => [...prev, tag].sort());
      setHasChanges(true);
    }
    setTagSearchQuery('');
    setDebouncedQuery('');
  };

  const handleRemoveTag = (tag: string) => {
    setFilterTags(prev => prev.filter(t => t !== tag));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate({
      orderQueuePaginationMode: paginationMode,
      orderQueuePageSize: pageSize,
      filterTags,
    });
  };

  const isLoading = settingsQuery.isLoading;
  const hasError = settingsQuery.isError;

  // Save button
  const saveButton = hasChanges ? (
    <Button
      onClick={handleSave}
      disabled={updateMutation.isPending}
      className="h-14 gap-2"
    >
      {updateMutation.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Save className="h-4 w-4" />
      )}
      {t('production.common.saveChanges')}
    </Button>
  ) : null;

  // Filter out already-selected tags from search results
  const availableSuggestions = (tagSearchResults.data ?? []).filter(
    tag => !filterTags.includes(tag)
  );

  const content = (
    <div className="space-y-6">
      {/* Error State */}
      {hasError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load order queue settings</AlertTitle>
          <AlertDescription className="flex items-center gap-2">
            {settingsQuery.error?.message || 'Unknown error'}
            <Button
              variant="link"
              className="p-0 h-auto"
              onClick={() => settingsQuery.refetch()}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading settings...</span>
        </div>
      )}

      {!isLoading && !hasError && (
        <>
          {/* Pagination Mode */}
          <div className="space-y-2">
            <Label htmlFor="pagination-mode" className="text-base font-medium">
              Display Mode
            </Label>
            <Select
              value={paginationMode}
              onValueChange={handlePaginationModeChange}
            >
              <SelectTrigger id="pagination-mode" className="w-full sm:w-[240px] h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="paginated" className="py-3">
                  Paginated (recommended)
                </SelectItem>
                <SelectItem value="endless" className="py-3">
                  Endless Scroll
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Paginated shows a fixed number of orders per page with navigation controls. Endless scroll loads more orders as you scroll down.
            </p>
          </div>

          {/* Page Size */}
          <div className="space-y-2">
            <Label htmlFor="page-size" className="text-base font-medium">
              Orders Per Page
            </Label>
            <Select
              value={String(pageSize)}
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger id="page-size" className="w-full sm:w-[240px] h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)} className="py-3">
                    {n} orders
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              How many orders to load per page. Lower values load faster. Can also be changed from the pagination bar on the Orders page.
            </p>
          </div>

          {/* Filter Tags */}
          <div className="space-y-3">
            <Label className="text-base font-medium">
              Tag Filters
            </Label>
            <p className="text-sm text-muted-foreground">
              Choose which Shopify order tags appear in the order queue tag filter. If none are selected, all tags will be shown.
            </p>

            {/* Searchable tag picker */}
            <Popover open={tagSearchOpen} onOpenChange={setTagSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={tagSearchOpen}
                  className="w-full sm:w-[320px] justify-start gap-2 h-11 font-normal text-muted-foreground"
                >
                  <Search className="h-4 w-4 shrink-0" />
                  Search tags...
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full sm:w-[320px] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Type to search tags..."
                    value={tagSearchQuery}
                    onValueChange={setTagSearchQuery}
                  />
                  <CommandList>
                    {debouncedQuery.length === 0 ? (
                      <div className="py-4 text-center text-sm text-muted-foreground">
                        Type to search order tags
                      </div>
                    ) : tagSearchResults.isLoading ? (
                      <div className="flex items-center justify-center py-4 gap-2">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Searching...</span>
                      </div>
                    ) : availableSuggestions.length === 0 ? (
                      <CommandEmpty>No matching tags found</CommandEmpty>
                    ) : (
                      <CommandGroup>
                        {availableSuggestions.map((tag) => (
                          <CommandItem
                            key={tag}
                            value={tag}
                            onSelect={() => handleAddTag(tag)}
                            className="cursor-pointer"
                          >
                            {tag}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Selected tags as removable badges */}
            {filterTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {filterTags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="gap-1 pr-1 text-sm font-normal">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-0.5 rounded-sm hover:bg-muted-foreground/20 p-0.5"
                      aria-label={`Remove ${tag}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            {filterTags.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {filterTags.length} tag{filterTags.length !== 1 ? 's' : ''} selected. Only these will appear in the tag filter.
              </p>
            )}
          </div>
        </>
      )}

      {/* ARIA live region */}
      <div role="status" aria-live="polite" className="sr-only">
        {updateMutation.isSuccess && 'Order queue settings saved successfully'}
        {hasChanges && 'You have unsaved changes'}
      </div>
    </div>
  );

  // If embedded, save button at top; otherwise at bottom of content
  if (embedded) {
    return (
      <div className="space-y-4">
        {saveButton && (
          <div className="flex justify-end">
            {saveButton}
          </div>
        )}
        {content}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {content}
      {saveButton && (
        <div className="flex justify-end">
          {saveButton}
        </div>
      )}
    </div>
  );
}
